import React, { useState } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const TicketSubmissionForm = ({ onClose, onSuccess }) => {
  const { isDarkMode } = useTheme();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    subject: '',
    category: '',
    priority: 'medium',
    description: '',
  });
  const [suggestedArticles, setSuggestedArticles] = useState([]);

  const categories = [
    { value: 'technical', label: 'Technical Issue' },
    { value: 'billing',   label: 'Billing & Payments' },
    { value: 'feature',   label: 'Feature Request' },
    { value: 'bug',       label: 'Bug Report' },
    { value: 'account',   label: 'Account Management' },
    { value: 'other',     label: 'Other' }
  ];

  const priorities = [
    { value: 'low',      label: 'Low — General inquiry' },
    { value: 'medium',   label: 'Medium — Needs attention' },
    { value: 'high',     label: 'High — Urgent issue' },
    { value: 'critical', label: 'Critical — System down' }
  ];

  const KB_SUGGESTIONS = [
    { keywords: ['payment', 'stripe', 'card', 'charge'], id: 11, title: 'Collecting Payments with Stripe' },
    { keywords: ['embed', 'iframe', 'website', 'install'], id: 14, title: 'Embedding Forms on Your Website' },
    { keywords: ['submission', 'response', 'follow up'], id: 12, title: 'Managing Submissions' },
    { keywords: ['logic', 'condition', 'show', 'hide', 'conditional'], id: 10, title: 'Conditional Logic — Show/Hide Fields' },
    { keywords: ['submit', 'submission', 'response', 'answer'], id: 16, title: 'Viewing and Managing Responses' },
    { keywords: ['analytics', 'metric', 'conversion', 'stat'], id: 15, title: 'Understanding Your Analytics' },
    { keywords: ['multi-step', 'steps', 'pages', 'progress'], id: 7, title: 'Multi-Step Forms' },
    { keywords: ['webhook', 'zapier', 'integration', 'connect'], id: 13, title: 'Webhooks' },
    { keywords: ['create', 'start', 'first', 'new form'], id: 1, title: 'Creating Your First Form' },
    { keywords: ['theme', 'colour', 'color', 'font', 'brand'], id: 4, title: 'Themes & Branding' },
    { keywords: ['plan', 'limit', 'upgrade', 'billing'], id: 17, title: 'Plans, Limits, and Upgrades' },
    { keywords: ['error', 'issue', 'problem', 'not work', 'broken'], id: 19, title: 'Troubleshooting Common Issues' },
  ];

  const handleDescriptionChange = (value) => {
    setFormData(prev => ({ ...prev, description: value }));
    if (value?.length > 20) {
      const lower = value.toLowerCase();
      const matched = KB_SUGGESTIONS.filter(s => s.keywords.some(kw => lower.includes(kw))).slice(0, 3);
      setSuggestedArticles(matched);
    } else {
      setSuggestedArticles([]);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!formData.subject.trim() || !formData.category || !formData.description.trim()) {
      setError('Please fill in all required fields.');
      return;
    }
    setLoading(true);
    setError(null);

    try {
      // 1 — Persist the ticket
      const { data, error: insertError } = await supabase
        .from('support_tickets')
        .insert({
          user_id:     user?.id   || null,
          user_email:  user?.email || null,
          subject:     formData.subject.trim(),
          category:    formData.category,
          priority:    formData.priority,
          description: formData.description.trim(),
          status:      'open',
        })
        .select('ticket_ref')
        .single();

      if (insertError) throw insertError;

      // 2 — Notify the support inbox; surface failure so the user knows to follow up by email
      const { error: notifyErr } = await supabase.functions.invoke('notify-support-ticket', {
        body: {
          ticket_ref:  data?.ticket_ref,
          subject:     formData.subject.trim(),
          category:    formData.category,
          priority:    formData.priority,
          description: formData.description.trim(),
        },
      });
      if (notifyErr) {
        console.warn('[ticket] Support notification failed:', notifyErr.message);
        // Non-blocking — ticket is already saved; let the user know via UI
        toast?.('Ticket saved. Email notification to support could not be sent — we will still review your ticket.', { icon: '⚠️', duration: 6000 });
      }

      onSuccess?.(data?.ticket_ref);
    } catch (err) {
      console.error('Ticket submission error:', err);
      setError('Failed to submit ticket. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className={`w-full max-w-2xl rounded-lg shadow-xl max-h-[90vh] overflow-y-auto ${isDarkMode ? 'bg-[#0a0f0d]' : 'bg-white'}`}>
        {/* Header */}
        <div className={`sticky top-0 p-6 border-b flex items-center justify-between ${isDarkMode ? 'bg-[#0a0f0d] border-[#1a2820]' : 'bg-white border-gray-200'}`}>
          <div>
            <h2 className="text-2xl font-bold text-foreground">Submit Support Ticket</h2>
            <p className="text-sm text-muted-foreground mt-1">Describe your issue and we'll get back to you within one business day</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-accent rounded-lg transition-colors">
            <Icon name="X" size={20} className="text-muted-foreground" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className={`p-4 rounded-lg border flex items-start gap-3 ${isDarkMode ? 'bg-red-500/10 border-red-500/20 text-red-400' : 'bg-red-50 border-red-200 text-red-700'}`}>
              <Icon name="AlertCircle" size={18} className="flex-shrink-0 mt-0.5" />
              <p className="text-sm">{error}</p>
            </div>
          )}

          <Input
            label="Subject"
            required
            placeholder="Brief description of your issue"
            value={formData.subject}
            onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Category <span className="text-destructive">*</span></label>
              <Select options={categories} value={formData.category} onChange={(v) => setFormData(prev => ({ ...prev, category: v }))} placeholder="Select category" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Priority</label>
              <Select options={priorities} value={formData.priority} onChange={(v) => setFormData(prev => ({ ...prev, priority: v }))} />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Description <span className="text-destructive">*</span></label>
            <textarea
              required
              rows={6}
              placeholder="Describe your issue in detail. Include any error messages, steps to reproduce, and what you expected to happen..."
              value={formData.description}
              onChange={(e) => handleDescriptionChange(e.target.value)}
              className={`w-full rounded-md border px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none ${
                isDarkMode ? 'bg-background border-[#1a2820] text-foreground' : 'bg-white border-gray-300 text-gray-900'
              }`}
            />
          </div>

          {suggestedArticles.length > 0 && (
            <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-[#0f1410] border-[#1a2820]' : 'bg-blue-50 border-blue-200'}`}>
              <div className="flex items-start gap-3">
                <Icon name="Lightbulb" size={20} className="text-blue-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h4 className="text-sm font-semibold text-foreground mb-2">These articles might resolve your issue:</h4>
                  <div className="space-y-2">
                    {suggestedArticles.map((article) => (
                      <a
                        key={article.id}
                        href={`/knowledge-base-articles?article=${article.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-sm text-primary hover:underline"
                      >
                        <Icon name="ExternalLink" size={13} />
                        {article.title}
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>Cancel</Button>
            <Button type="submit" variant="default" loading={loading} iconName="Send">Submit Ticket</Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TicketSubmissionForm;
